import {
  BoundNumberField,
  Button,
  Chip,
  collapseColumn,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  numericColumn,
  ScrollableContent,
  ScrollableFooter,
  ScrollableParent,
  simpleHeader,
  TabsWithContent,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, useFormState } from "@homebound/form-state";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useHistory } from "react-router-dom";
import { Loading, Price } from "src/components";
import { baseDownloadUrl } from "src/context";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { BidPackageStatus, SavePlanBasedBidContractLineItemInput } from "src/generated/graphqlTypes";
import { AddTradePartnerModal } from "src/routes/bids/components/AddTradePartnerModal";
import { pluralize, queryResult } from "src/utils";
import { openInSelf } from "src/utils/window";
import {
  PlanBasedBidsTableV2_BidContractRevisionFragment,
  PlanBasedBidsTableV2_BidPackageFragment,
  PlanBasedBidsTableV2_BidPackageRequestFragment,
  PlanBasedBidsTableV2_BidPlanChildGroupFragment,
  PlanBasedBidsTableV2_BidPlanChildGroupLineFragment,
  PlanBasedBidsTableV2_BidPlanGroupFragment,
  usePlanBasedBidsTableV2_BidPlanChildGroupsQuery,
} from "./PlanBasedBidsTableV2.generated";

/** Holds each POF's data as the user opens its tab, so we don't have to load them all up front. */
type LoadedByPof = Record<string, readonly PlanBasedBidsTableV2_BidPlanGroupFragment[]>;

type PlanBasedBidsTableV2Props = {
  bidPackage: PlanBasedBidsTableV2_BidPackageFragment;
  searchFilter?: string;
  showDownloadButton?: boolean;
} & (
  | { mode?: "view"; tradePartnerId?: undefined; onSave?: undefined }
  | {
      mode: "edit";
      tradePartnerId: string;
      onSave: (input: SavePlanBasedBidContractLineItemInput[]) => void | Promise<void>;
    }
);

export function PlanBasedBidsTableV2(props: PlanBasedBidsTableV2Props) {
  const { bidPackage, searchFilter, showDownloadButton } = props;
  const mode = props.mode ?? "view";
  const sortedPofs = bidPackage.productOfferings.sortBy((po) => po.name);
  const history = useHistory();
  const [selectedTab, setSelectedTab] = useState<string>(sortedPofs[0].id);
  const query = usePlanBasedBidsTableV2_BidPlanChildGroupsQuery({
    variables: { bidPackageId: bidPackage.id, productOfferingId: selectedTab },
  });

  // The trade partner's existing BCR (undefined for first-time bid).
  const bcr =
    mode === "edit"
      ? bidPackage.latestVersion.latestBidContractRevisions.find(
          (b) => b.bidContract.tradePartner?.id === props.tradePartnerId,
        )
      : undefined;

  // Single formState across all POF tabs. The table query still fires per active
  // tab, but each tab's `bidPlanGroups` are merged into `loadedByPof` and passed
  // as form-state's reactive `init.input`. form-state diffs the cost list by id,
  // so user edits survive when a new tab loads and the input grows.
  const [loadedByPof, setLoadedByPof] = useState<LoadedByPof>({});
  useEffect(() => {
    if (mode !== "edit") return;
    const data = query.data?.bidPackage.bidPlanGroups;
    const pofId = query.variables?.productOfferingId;
    if (!data || !pofId) return;
    setLoadedByPof((prev) => (prev[pofId] === data ? prev : { ...prev, [pofId]: data }));
  }, [mode, query]);

  const formState = useFormState({
    readOnly: mode !== "edit",
    config: editFormConfig,
    init: { input: loadedByPof, map: (loaded) => buildEditCosts(loaded, bcr?.id, bidPackage.costCodes) },
  });

  async function handleSave() {
    // Filter to dirty rows (a row is dirty if the user changed totalCostInCents.
    // Read full row.value rather than `changedValue` because we always
    // need groupLineId/costCodeId on the wire, not just dirty fields.
    const input = formState.costs.rows
      .filter((r) => r.dirty)
      .map((r) => ({
        id: r.value.pbbcliId,
        totalCostInCents: r.value.totalCostInCents,
        costCodeId: r.value.costCodeId,
        groupLineId: r.value.groupLineId,
      }));
    await props.onSave?.(input);
  }

  return (
    <>
      <ScrollableParent>
        <ScrollableContent virtualized>
          <TabsWithContent
            alwaysShowAllTabs
            tabs={sortedPofs.map((pof) => ({
              name: pof.name,
              value: pof.id,
              render: () => {
                if (query.loading) return <Loading />;
                return queryResult(query, ({ bidPackage: { bidPlanGroups } }) => (
                  <>
                    {bidPlanGroups.sortByKey("order").flatMap((bpg) =>
                      bpg.childGroups.map((bpcg) => (
                        <div css={Css.mb2.$} key={bpcg.id}>
                          <PlanBasedBidsTableView
                            bidPackage={bidPackage}
                            bidPlanGroup={bpg}
                            childGroup={bpcg}
                            productOffering={pof}
                            searchFilter={searchFilter}
                            mode={mode}
                            tradePartnerId={mode === "edit" ? props.tradePartnerId : undefined}
                            formState={mode === "edit" ? formState : undefined}
                          />
                        </div>
                      )),
                    )}
                  </>
                ));
              },
            }))}
            selected={selectedTab}
            onChange={setSelectedTab}
            right={
              showDownloadButton &&
              mode === "view" && (
                <Button
                  icon="download"
                  label="Download Bidsheet"
                  onClick={() => downloadPlanBasedBidPackageV2(bidPackage.id)}
                  variant="secondaryBlack"
                  size="md"
                />
              )
            }
          />
        </ScrollableContent>
      </ScrollableParent>
      {mode === "edit" && (
        <ScrollableFooter>
          <div css={Css.df.px6.py3.bshHover.bgWhite.$}>
            <div css={Css.mla.df.gap2.$}>
              <Button size="lg" onClick={() => history.goBack()} label="Cancel" variant="quaternary" />
              <Button size="lg" onClick={handleSave} label="Save" />
            </div>
          </div>
        </ScrollableFooter>
      )}
    </>
  );
}

export function downloadPlanBasedBidPackageV2(bidPackageId: string) {
  const params = new URLSearchParams({ type: "planBasedBidPackageV2", bidPackageId });
  openInSelf(`${baseDownloadUrl()}/xlsx?${params.toString()}`);
}

type PlanBasedBidsTableViewProps = {
  bidPackage: PlanBasedBidsTableV2_BidPackageFragment;
  bidPlanGroup: PlanBasedBidsTableV2_BidPlanGroupFragment;
  childGroup: PlanBasedBidsTableV2_BidPlanChildGroupFragment;
  productOffering: NamedFragment;
  searchFilter?: string;
  mode: "view" | "edit";
  tradePartnerId: string | undefined;
  formState: ObjectState<EditFormValues> | undefined;
};

function PlanBasedBidsTableView(props: PlanBasedBidsTableViewProps) {
  const { bidPackage, bidPlanGroup, childGroup, productOffering, searchFilter, mode, tradePartnerId, formState } =
    props;
  const { openModal } = useModal();
  const activeRequests = bidPackage.activeRequests;
  const bidContractRevisions = bidPackage.latestVersion.latestBidContractRevisions;
  const hasTrades = !activeRequests.isEmpty;
  // Hide the inline Add Trade link in the create flow (status === Draft) — the bid
  // package isn't open for bidding yet, so adding a trade isn't actionable.
  const canAddTrade = mode === "view" && !hasTrades && bidPackage.status.code !== BidPackageStatus.Draft;

  // In edit mode we render only the selected trade's columns (other trades are hidden).
  const requests = useMemo(
    () => (mode === "edit" ? activeRequests.filter((r) => r.tradePartner.id === tradePartnerId) : activeRequests),
    [mode, activeRequests, tradePartnerId],
  );

  const costRowLookup = useCallback(
    (bpglId: string, costCodeId: string) =>
      formState?.costs.rows.find((r) => r.value.groupLineId === bpglId && r.value.costCodeId === costCodeId),
    [formState],
  );

  const rows = useMemo(() => createRows(bidPlanGroup, childGroup), [bidPlanGroup, childGroup]);
  const columns = useMemo(
    () =>
      createColumns({
        bidPlanGroup,
        productOffering,
        activeRequests: requests,
        bidContractRevisions,
        costCodes: bidPackage.costCodes,
        addTradeAction: canAddTrade
          ? () => openModal({ content: <AddTradePartnerModal bidPackage={bidPackage} /> })
          : undefined,
        mode,
        costRowLookup,
      }),
    [
      bidPlanGroup,
      productOffering,
      requests,
      bidContractRevisions,
      bidPackage,
      canAddTrade,
      openModal,
      mode,
      costRowLookup,
    ],
  );
  return (
    <div>
      <TableHeader bidPlanGroup={bidPlanGroup} childGroup={childGroup} />
      <GridTable columns={columns} filter={searchFilter} rows={rows} id={childGroup.id} />
    </div>
  );
}

type HeaderRow = { kind: "header"; data: undefined };
type DisplayByTypeId = Record<string, { name: string; quoted: boolean }>;
type CostLineRow = {
  kind: "costLine";
  data: { bidPlanChildGroupLine: PlanBasedBidsTableV2_BidPlanChildGroupLineFragment; display: DisplayByTypeId };
};
type Row = HeaderRow | CostLineRow;

// Layout: collapse + dimension columns + flex spacer + (per-trade × cost-code columns | Add Trade link).
// The trade-partner name is stacked above the cost code in the first cost-code column of each
// trade; subsequent columns show only the cost code. Cell costs come from PB-BCLIs joined to
// the trade's BCR (matched by tradePartner.id), so an unsubmitted request renders empty cells.
function createColumns(props: {
  bidPlanGroup: PlanBasedBidsTableV2_BidPlanGroupFragment;
  productOffering: NamedFragment;
  activeRequests: readonly PlanBasedBidsTableV2_BidPackageRequestFragment[];
  bidContractRevisions: readonly PlanBasedBidsTableV2_BidContractRevisionFragment[];
  costCodes: readonly NamedFragment[];
  addTradeAction: (() => void) | undefined;
  mode: "view" | "edit";
  costRowLookup: (bpglId: string, costCodeId: string) => ObjectState<CostInput> | undefined;
}): GridColumn<Row>[] {
  const {
    bidPlanGroup,
    productOffering,
    activeRequests,
    bidContractRevisions,
    costCodes,
    addTradeAction,
    mode,
    costRowLookup,
  } = props;
  const hasTrades = !activeRequests.isEmpty;

  // Fixed widths so dimension columns stay tight to the left; a flex spacer below absorbs
  // any leftover horizontal space and pushes trade columns to the right edge.
  // When the group has no dimensions the backend emits a single options=[] line so the
  // group renders as one row labeled with the product offering's name (a "base only" cost).
  const dimensionColumns = bidPlanGroup.dimensions.isEmpty
    ? [column<Row>({ header: "Plan", costLine: productOffering.name, w: "300px" })]
    : bidPlanGroup.dimensions.map((dim) =>
        column<Row>({
          header: dim.globalOptionType.name,
          costLine: ({ display }) => {
            const displayValue = display[dim.globalOptionType.id];
            if (!displayValue) return emptyCell;
            return displayValue.quoted ? '"' : displayValue.name;
          },
          w: "300px",
        }),
      );

  const spacerColumn = column<Row>({ header: emptyCell, costLine: () => emptyCell, w: 1 });

  if (!hasTrades) {
    // Once any request exists this column disappears in favor of the (request × costCode) leaf columns below
    return [
      collapseColumn<Row>(),
      ...dimensionColumns,
      spacerColumn,
      column<Row>({
        header: () =>
          addTradeAction ? <Button label="Add Trade" variant="text" onClick={addTradeAction} /> : emptyCell,
        costLine: () => emptyCell,
        w: "300px",
      }),
    ];
  }

  const tradeColumns = activeRequests.flatMap((request) => {
    const bcr = bidContractRevisions.find((b) => b.bidContract.tradePartner?.id === request.tradePartner.id);
    return costCodes.map((cc, idx) => {
      const isFirstCostCodeForTrade = idx === 0;
      return numericColumn<Row>({
        // First cost-code column for each trade stacks the trade name above the cost-code label;
        // subsequent columns render an invisible placeholder in the trade-name slot so the
        // cost-code labels stay aligned across all trade columns.
        header: {
          content: (
            <div css={Css.df.fdc.w100.$}>
              <span css={Css.smSb.gray900.lineClamp1.if(!isFirstCostCodeForTrade).vh.$}>
                {isFirstCostCodeForTrade ? request.tradePartner.name : "."}
              </span>
              <span css={Css.xsSb.gray700.$}>{cc.name}</span>
            </div>
          ),
          alignment: "left",
        },
        costLine: ({ bidPlanChildGroupLine }) => {
          if (mode === "edit") {
            const row = costRowLookup(bidPlanChildGroupLine.id, cc.id);
            if (!row) return emptyCell;
            return { content: <BoundNumberField field={row.totalCostInCents} type="cents" />, alignment: "left" };
          }
          if (!bcr) return emptyCell;
          const pbbcli = bidPlanChildGroupLine.planBasedBidContractLineItems.find(
            (pbbcli) => pbbcli.revision.id === bcr.id && pbbcli.costCode.id === cc.id,
          );
          return { content: <Price valueInCents={pbbcli?.totalCostInCents} />, alignment: "left" };
        },
        w: "300px",
      });
    });
  });

  return [collapseColumn<Row>(), ...dimensionColumns, spacerColumn, ...tradeColumns];
}

// Simple header followed by one parent row per first-dimension option (e.g. Elevation).
// Sibling cost lines that share the same first-dimension option nest as collapsible children.
function createRows(
  bidPlanGroup: PlanBasedBidsTableV2_BidPlanGroupFragment,
  childGroup: PlanBasedBidsTableV2_BidPlanChildGroupFragment,
): GridDataRow<Row>[] {
  const firstDimensionTypeId = bidPlanGroup.dimensions[0]?.globalOptionType.id;
  const groups = childGroup.lines.groupBy(
    (bpcgl) => bpcgl.options.find((o) => o.globalOption.type.id === firstDimensionTypeId)?.globalOption.id ?? "",
  );

  const parentRows = groups.toValues().map((bpcgls) => {
    const [parent, ...children] = buildCostLineRows(bpcgls);
    return { ...parent, children };
  });

  return [simpleHeader, ...parentRows];
}

// Each dimension cell renders the option name, or quoted when it repeats the predecessor's value.
function buildCostLineRows(bpcgls: PlanBasedBidsTableV2_BidPlanChildGroupLineFragment[]): GridDataRow<CostLineRow>[] {
  return bpcgls.reduce<GridDataRow<CostLineRow>[]>((acc, curr, currentIndex) => {
    const prevDisplay = currentIndex > 0 ? (acc[currentIndex - 1]?.data?.display ?? {}) : {};
    const optionsByTypeId = curr.options.mapToObject((rpo) => {
      const typeId = rpo.globalOption.type.id;
      const name = rpo.globalOption.name;
      return [typeId, { name, quoted: name === prevDisplay[typeId]?.name }] as const;
    });
    return [...acc, { kind: "costLine", id: curr.id, data: { bidPlanChildGroupLine: curr, display: optionsByTypeId } }];
  }, []);
}

function TableHeader(props: Pick<PlanBasedBidsTableViewProps, "bidPlanGroup" | "childGroup">) {
  const { bidPlanGroup, childGroup } = props;
  // Count distinct globalOption IDs per dimension type from the current POF's child group lines
  const countsByDimensionType = useMemo(() => {
    const allOptions = bidPlanGroup.childGroups.flatMap((cg) => cg.lines.flatMap((line) => line.options));
    const grouped = allOptions.groupBy((opt) => opt.globalOption.type.id);
    return grouped
      .toEntries()
      .mapToObject(([typeId, opts]) => [typeId, opts.map((o) => o.globalOption.id).unique().length]);
  }, [bidPlanGroup]);

  return (
    <div css={Css.df.aic.gap2.px2.py1.bgWhite.bb.bcGray200.$}>
      <span data-testid="tableHeader" css={Css.mdSb.gray900.$}>
        {childGroup.displayName}
      </span>
      {bidPlanGroup.dimensions.map((dim) => {
        const count = countsByDimensionType[dim.globalOptionType.id] ?? 0;
        return <Chip key={dim.id} text={`${count} ${pluralize(count, dim.globalOptionType.name)}`} />;
      })}
    </div>
  );
}

type CostInput = {
  id: string | undefined;
  pbbcliId: string | undefined;
  groupLineId: string;
  costCodeId: string;
  totalCostInCents: number | null | undefined;
};

type EditFormValues = { costs: CostInput[] };

const editFormConfig: ObjectConfig<EditFormValues> = {
  costs: {
    type: "list",
    // update: incremental since we want only the rows the user touched on changedValue
    update: "incremental",
    config: {
      id: { type: "value" },
      pbbcliId: { type: "value" },
      groupLineId: { type: "value" },
      costCodeId: { type: "value" },
      totalCostInCents: { type: "value" },
    },
  },
};

// Build the cost list (one per BPGL × CostCode) from every PO's loaded bidPlanGroups.
// Existing PB-BCLIs (matched by `(bcr.id, costCode.id)`) seed their `pbbcliId` and current
// `totalCostInCents`; missing ones get a synthetic id so form-state can still match the
// row across re-inits when more tabs load.
function buildEditCosts(
  loaded: LoadedByPof,
  bcrId: string | undefined,
  costCodes: readonly NamedFragment[],
): EditFormValues {
  const bpcgls = loaded
    .toValues()
    .flatMap((bidPlanGroups) => bidPlanGroups.flatMap((bpg) => bpg.childGroups))
    .flatMap((bpcg) => bpcg.lines);

  const costs = bpcgls.flatMap((bpcgl) =>
    costCodes.map((cc) => {
      const pbbcli = bcrId
        ? bpcgl.planBasedBidContractLineItems.find((p) => p.revision.id === bcrId && p.costCode.id === cc.id)
        : undefined;
      return {
        id: pbbcli?.id ?? `${bpcgl.id}:${cc.id}`,
        pbbcliId: pbbcli?.id,
        groupLineId: bpcgl.id,
        costCodeId: cc.id,
        totalCostInCents: pbbcli?.totalCostInCents,
      };
    }),
  );
  return { costs };
}
